import { useTheme, type DefaultTheme } from 'styled-components';
import { DealStatusEnum } from '../../types/types';
import { Box } from '../Core';
import { IconName } from '../Icons';
import { Icon, type IconProps } from '../Icons/Icon';
import { IndicatorDotVariants, IndicatorDotWrapper } from '../IndicatorDot';
import { Tooltip } from '../Tooltip';
import { Wrapper } from './styles';
import { OrderStatusSize } from './types';

type DealStatusDisplay = {
  icon: IconName;
  color: string;
  variant: IndicatorDotVariants;
};

const getDealStatusDisplay = (status: DealStatusEnum | undefined, theme: DefaultTheme): DealStatusDisplay => {
  switch (status) {
    case DealStatusEnum.Live:
      return {
        icon: IconName.Circle,
        color: theme.colors.blue.lighten,
        variant: IndicatorDotVariants.Warning,
      };
    case DealStatusEnum.Canceled:
    case DealStatusEnum.Expired:
      return {
        icon: IconName.CloseCircleSolid,
        color: theme.colors.red.default,
        variant: IndicatorDotVariants.Negative,
      };
    case DealStatusEnum.Pending:
      return {
        icon: IconName.Clock,
        color: theme.colors.blue.lighten,
        variant: IndicatorDotVariants.Warning,
      };
    case DealStatusEnum.New:
      return {
        icon: IconName.Clock,
        color: theme.colors.blue.lighten,
        variant: IndicatorDotVariants.Warning,
      };
    default:
      return {
        icon: IconName.Circle,
        color: theme.colors.gray.main,
        variant: IndicatorDotVariants.Warning,
      };
  }
};

/**
 * Status icon and display for Deals
 */
const DealStatusIcon = ({
  dealStatus,
  ...props
}: { dealStatus: DealStatusEnum; theme?: DefaultTheme } & Omit<IconProps, 'color' | 'icon'>) => {
  const defaultTheme = useTheme();
  const theme = props.theme ?? defaultTheme;
  const config = getDealStatusDisplay(dealStatus, theme);

  return <Icon icon={config.icon} color={config.color} {...props} />;
};

type DealStatusProps = {
  status: DealStatusEnum;
  align?: 'right' | 'left';
  iconPlacement?: 'right' | 'left';
  tooltipText?: string;
  theme?: DefaultTheme;
  title?: string;
  size?: OrderStatusSize;
};

export function DealStatus({
  status,
  align = 'right',
  tooltipText,
  size = OrderStatusSize.Default,
  ...props
}: DealStatusProps) {
  const defaultTheme = useTheme();
  const theme = props.theme ?? defaultTheme;
  const config = getDealStatusDisplay(status, theme);

  const tooltipContent = !!tooltipText && <Box maxWidth="300px">{tooltipText}</Box>;

  return tooltipContent ? (
    <Tooltip tooltip={tooltipContent} usePortal>
      {renderDealStatus()}
    </Tooltip>
  ) : (
    renderDealStatus()
  );

  function renderDealStatus() {
    const iconSize = size === OrderStatusSize.Default ? 'fontSizeLarge' : 'fontSizeSmall';
    return (
      <Wrapper iconPlacement="left" align={align} theme={theme} size={size} {...props} data-testid="deal-status">
        {status}
        <IndicatorDotWrapper show={!!tooltipText} theme={theme} variant={config.variant}>
          <DealStatusIcon dealStatus={status} theme={theme} size={iconSize} />
        </IndicatorDotWrapper>
      </Wrapper>
    );
  }
}
